import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  GitBranch,
  Users,
  CreditCard,
  Crown,
  Receipt,
  Sliders,
  KeyRound,
  RotateCcw,
  Sparkles,
  HelpCircle,
  LogOut,
  Activity,
  Menu,
  X,
  Search,
  ExternalLink,
  RefreshCw,
  ArrowUpRight,
  Trophy,
  Smartphone
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { rtdb } from "../firebase/firebaseClient";
import { Toaster } from "sonner";

const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/dialog-manager", label: "Dialog Mod Manager", icon: Smartphone, tag: "MOD" },
  { path: "/audit", label: "Source & Audit Monitor", icon: Activity },
  { path: "/apps", label: "Apps Manager", icon: Layers, badgeKey: "appsCount" },
  { path: "/versions", label: "Versions", icon: GitBranch },
  { path: "/users", label: "Users Directory", icon: Users, badgeKey: "usersCount" },
  { path: "/seasons", label: "Season Rewards", icon: Trophy },
  { path: "/credits", label: "Credits & Promo", icon: CreditCard },
  { path: "/vip", label: "VIP Suite", icon: Crown, badgeKey: "vipRequests" },
  { path: "/transactions", label: "Transactions", icon: Receipt },
  { path: "/system", label: "System Settings", icon: Sliders },
  { path: "/keys", label: "API Keys", icon: KeyRound },
  { path: "/updates", label: "Update Requests", icon: RotateCcw, badgeKey: "updateRequests" },
  { path: "/events", label: "Events & Quests", icon: Sparkles },
  { path: "/faq", label: "FAQ & Inbox", icon: HelpCircle, badgeKey: "faqQuestions" }
];

function BerryLogo({ className = "w-8 h-8" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1080 1080"
      preserveAspectRatio="xMidYMid meet"
      className={className}
    >
      <path
        transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)"
        d="M270.63 377.35 C317 374.69 329.48 396.32 357.39 429.16 L401.48 481.02 L565.85 673.76 C669.46 582.58 818.06 522.37 954.92 509.41 C1146.82 490.25 1338.31 549.54 1485.82 673.79 C1537.52 611.7 1591.35 549.75 1643.8 488.18 L1692.26 431.31 C1712.07 408.1 1728.09 382.69 1760.16 377.68 C1778.22 374.82 1796.68 379.26 1811.46 390.03 C1825.95 400.75 1835.6 416.77 1838.29 434.59 C1840.62 449.39 1837.98 464.55 1830.79 477.7 C1824.2 489.78 1812.51 502.23 1803.41 512.83 L1771.74 549.95 L1644.32 699.49 L1604.45 746.12 C1598.58 753 1588.49 764.15 1583.57 770.97 L1582.41 772.61 C1657.13 869.77 1697.09 951.05 1724.72 1071.18 C1732.47 1106.26 1736.77 1143.2 1739.18 1179.15 C1740.03 1191.94 1739.9 1210.32 1740.44 1223.71 C1741.2 1224.46 1741.95 1225.22 1742.71 1225.98 C1743.5 1314.86 1743.14 1473.69 1743.16 1555.83 L308.12 1555.81 L308.07 1338.36 C308.07 1305.49 308 1272.44 308.17 1239.56 C308.32 1229.85 310.93 1220.05 311.14 1210.37 C311.83 1179.22 314.12 1148.78 318.46 1117.89 C336.7 991.33 388.77 872.04 469.15 772.6 C450.27 749.03 427.81 723.73 408.06 700.57 L295.16 568.2 L252.54 518.21 C238.04 501.19 219.2 482.83 214.13 460.7 C209.89 442.67 213.12 423.7 223.08 408.09 C234.66 389.94 250.27 381.78 270.63 377.35 Z"
        fill="#436AE7"
      />
      <path
        transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)"
        d="M1263.22 921.45 C1321.98 914.45 1375.28 956.41 1382.27 1015.17 C1389.25 1073.93 1347.27 1127.23 1288.51 1134.2 C1229.77 1141.16 1176.5 1099.2 1169.52 1040.46 C1162.54 981.73 1204.48 928.45 1263.22 921.45 Z"
        fill="#E5E6FB"
      />
      <path
        transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)"
        d="M763.07 921.43 C821.93 914.53 875.21 956.73 881.97 1015.6 C888.73 1074.48 846.4 1127.65 787.51 1134.27 C728.82 1140.86 675.86 1098.71 669.13 1040.04 C662.39 981.36 704.42 928.31 763.07 921.43 Z"
        fill="#E5E6FB"
      />
    </svg>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [badges, setBadges] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchBadgeCounts = async () => {
    try {
      const [vipReqs, updateReqs, userQs] = await Promise.all([
        rtdb.get("requests/vip_requests").catch(() => null),
        rtdb.get("requests/update_requests").catch(() => null),
        rtdb.get("requests/user_questions").catch(() => null)
      ]);

      const countPending = (obj) => {
        if (!obj || typeof obj !== "object") return 0;
        return Object.values(obj).filter((item) => item?.status === "pending" || !item?.status).length;
      };

      setBadges({
        vipRequests: countPending(vipReqs),
        updateRequests: countPending(updateReqs),
        faqQuestions: countPending(userQs)
      });
    } catch (e) {
      console.warn("Badge count warning:", e);
    }
  };

  useEffect(() => {
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      } else if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchBadgeCounts();
    setTimeout(() => {
      setIsRefreshing(false);
      window.dispatchEvent(new CustomEvent("bma-admin-refresh"));
    }, 400);
  };

  const filteredNavs = NAV_ITEMS.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans selection:bg-primary selection:text-white relative">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="fixed inset-0 tech-grid opacity-15 pointer-events-none z-0" />

      {/* Sticky Header */}
      <header className="sticky top-0 z-[60] h-16 border-b border-white/5 bg-[#05060b]/90 backdrop-blur-xl px-4 sm:px-6 flex items-center justify-between shadow-lg">
        {/* Mobile Menu Toggle & Brand */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg border border-white/10 bg-[#07080f] text-white/70 hover:text-white"
          >
            <Menu size={18} />
          </button>

          <Link to="/" className="flex items-center gap-2.5 group">
            <BerryLogo className="w-8 h-8 group-hover:scale-105 transition-transform shrink-0" />
            <span className="font-display font-bold text-base tracking-tight text-white">
              Berry Admin
            </span>
          </Link>
        </div>

        {/* Global Search Bar (Trigger) */}
        <div className="hidden md:flex items-center">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-3 px-3.5 py-1.5 rounded-lg border border-white/10 bg-[#07080f] text-xs text-white/50 hover:border-primary/50 hover:text-white transition-all w-64 shadow-sm"
          >
            <Search size={14} className="text-primary" />
            <span className="flex-1 text-left">Search anything...</span>
            <kbd className="px-1.5 py-0.5 text-[10px] font-mono-tech border border-white/10 rounded text-white/40">
              Ctrl+K
            </kbd>
          </button>
        </div>

        {/* Right Header Actions */}
        <div className="flex items-center gap-3 text-xs">
          {/* Status Indicator */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-[#07080f] text-[11px]">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-white/60">Live</span>
          </div>

          <button
            onClick={handleManualRefresh}
            title="Refresh Realtime Data"
            className="p-2 rounded-lg border border-white/10 bg-[#07080f] text-white/60 hover:text-primary hover:border-primary transition-colors"
          >
            <RefreshCw size={15} className={isRefreshing ? "animate-spin text-primary" : ""} />
          </button>

          <a
            href="https://berrymodapk.com"
            target="_blank"
            rel="noopener noreferrer"
            title="Open Public Site (berrymodapk.com)"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-[#07080f] text-white/60 hover:text-white hover:border-white/20 transition-colors"
          >
            <span>Public Site</span>
            <ExternalLink size={12} />
          </a>

          {/* User Profile */}
          <div className="flex items-center gap-2 pl-2 border-l border-white/10">
            <div className="hidden md:flex flex-col text-right">
              <span className="text-xs font-semibold text-white truncate max-w-[120px]">
                {user?.displayName || "Administrator"}
              </span>
              <span className="text-[10px] text-white/40 truncate max-w-[120px]">
                {user?.email}
              </span>
            </div>
            <button
              onClick={logout}
              title="Sign Out"
              className="p-2 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive hover:text-white transition-colors"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div className="flex-1 flex relative z-10">
        {/* Mobile Backdrop Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-[140] lg:hidden transition-opacity"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Modern Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 lg:top-16 z-[150] lg:z-30 h-screen lg:h-[calc(100vh-4rem)] w-64 max-w-[85vw] border-r border-white/5 bg-[#05060b]/98 lg:bg-[#05060b]/60 backdrop-blur-2xl flex flex-col justify-between transition-transform duration-300 ease-out shadow-2xl lg:shadow-none ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="p-3.5 space-y-1 overflow-y-auto flex-1 custom-scrollbar">
            <div className="flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/40">
              <span>Navigation</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1 text-white/40 hover:text-white"
              >
                <X size={14} />
              </button>
            </div>

            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname === item.path || location.pathname.startsWith(item.path + "/");
              const badgeCount = item.badgeKey ? badges[item.badgeKey] : null;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-medium transition-all ${
                    isActive
                      ? "bg-primary text-white font-semibold shadow-md shadow-primary/20"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={16} className={isActive ? "text-white" : "text-white/50"} />
                    <span>{item.label}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    {item.tag && (
                      <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded-md bg-primary/20 text-primary border border-primary/30 font-mono-tech">
                        {item.tag}
                      </span>
                    )}
                    {badgeCount > 0 && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary/20 text-primary border border-primary/30 font-mono-tech">
                        {badgeCount}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="p-4 border-t border-white/5 text-[11px] text-white/40 space-y-1 bg-black/30">
            <div className="flex justify-between">
              <span>Database</span>
              <span className="text-white/70">Firebase RTDB</span>
            </div>
            <div className="flex justify-between">
              <span>Auth</span>
              <span className="text-primary font-medium">Google Verified</span>
            </div>
          </div>
        </aside>

        {/* Content View */}
        <main className="flex-1 p-3.5 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full overflow-x-hidden pb-12">
          {children}
        </main>
      </div>

      {/* Search Palette Modal */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[160] bg-black/80 backdrop-blur-md flex items-start justify-center pt-16 sm:pt-20 p-3 sm:p-4 animate-in fade-in duration-150"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="w-full max-w-lg border border-white/10 bg-[#07080f] rounded-2xl p-4 space-y-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 pb-3 border-b border-white/10">
              <Search size={16} className="text-primary" />
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search module..."
                className="w-full bg-transparent text-sm text-white placeholder-white/40 outline-none"
              />
              <button onClick={() => setSearchOpen(false)} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto space-y-1 text-xs">
              {filteredNavs.map((nav) => (
                <button
                  key={nav.path}
                  onClick={() => {
                    navigate(nav.path);
                    setSearchOpen(false);
                    setSearchQuery("");
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-xl text-left text-white/70 hover:bg-primary hover:text-white transition-colors"
                >
                  <span className="font-medium">{nav.label}</span>
                  <ArrowUpRight size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Action Notification Toaster */}
      <Toaster richColors position="top-right" theme="dark" closeButton />
    </div>
  );
}
